import React, { useState, ReactNode } from 'react';
import { MenuItem, Select, SelectProps, Skeleton, Tooltip, Typography, Theme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Control, FieldError, FieldValues, Path, PathValue, useController } from 'react-hook-form';
import { getErrorMessage } from '../Input/ValidationErrors';
import { ErrorMessage } from '/packages/shared-components/ErrorMessage/ErrorMessage';
import InputLabelText from '../InputLabelText/InputLabelText';

export interface SelectInputProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  name: Path<T>;
  options: {
    value: string;
    label: ReactNode;
    tooltip?: string;
    subLabel?: string;
  }[];
  label?: string | null;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  showModified?: boolean;
  placeholder?: string;
  helperText?: string;
  showOptionalLabel?: boolean;
  changeRequested?: boolean;
  loading?: boolean;
  labelTextColor?: string;
  fetchError?: Error;
  renderValue?: SelectProps<PathValue<T, Path<T>>>['renderValue'];
  standardStyle?: boolean;
}

const SelectInput = <T extends FieldValues>({
  control,
  name,
  options,
  label,
  required = false,
  disabled = false,
  hideLabel = false,
  showModified = false,
  placeholder,
  helperText,
  showOptionalLabel,
  changeRequested = false,
  loading = false,
  fetchError,
  labelTextColor,
  renderValue = undefined,
  standardStyle = false,
}: SelectInputProps<T>) => {
  const [open, setOpen] = useState(false);
  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      required: required ? 'This field is required.' : false,
    },
  });

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);

  const isModified = showModified && fieldState.isDirty;

  const error = fieldState.error as FieldError;
  const errorMessage = getErrorMessage(error);

  const changeRequestedStyles = changeRequested
    ? {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: (theme: Theme) => theme.palette.changeRequested.main,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: (theme: Theme) => theme.palette.changeRequested.main,
        },
      }
    : {};

  const getLabel = (value: string): string =>
    typeof value === 'string' ? String(options.find((el) => el.value === value)?.label || value) : String(value);

  const renderFn = placeholder
    ? (value: PathValue<T, Path<T>>) => {
        if (typeof value === 'string') return value === '' ? placeholder : getLabel(value);
        if (value === undefined || value === null) return placeholder;
        if (Array.isArray(value)) return value.length === 0 ? placeholder : value.map(getLabel).join(', ');
        return value;
      }
    : undefined;

  return (
    <InputLabelText
      label={label}
      name={name}
      required={showOptionalLabel !== undefined ? !showOptionalLabel : required}
      errorMessage={errorMessage}
      hideLabel={hideLabel}
      showModified={isModified}
      helperText={helperText}
      changeRequested={changeRequested}
      disabled={disabled}
      color={labelTextColor}
    >
      {loading ? (
        <Skeleton width="100%" height="64px" />
      ) : fetchError ? (
        <ErrorMessage errorMsg={fetchError.message} />
      ) : (
        <Select
          {...field}
          sx={
            standardStyle
              ? { width: '72px', height: '40px', float: 'right', marginTop: '16px', marginBottom: '16px' }
              : {
                  background: (theme) => theme.palette.background.paper,
                  color: field.value === '' ? 'text.secondary' : '',
                  ...changeRequestedStyles,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.info.main,
                  },
                }
          }
          error={!!error}
          variant="outlined"
          labelId={`${name}-label`}
          disabled={disabled}
          displayEmpty={true}
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
          fullWidth={true}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            PaperProps: {
              sx: {
                boxShadow: '0 2px 4px -1px rgba(0,0,0,0.06), 0 4px 6px -1px rgba(0,0,0,0.1)',
                borderRadius: 1,
              },
            },
            sx: {
              maxHeight: label === 'Role' ? '290px' : 'auto',
            },
          }}
          renderValue={renderValue ?? renderFn}
          data-cy={`select-${name}`}
        >
          {options?.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={
                standardStyle
                  ? {
                      minHeight: '30px',
                      width: '100%',
                      padding: '6px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      margin: 0,
                    }
                  : { margin: 0 }
              }
              data-cy={`option-${option.value}`}
            >
              <Typography variant="body1" component="span" sx={{ flexGrow: 1 }}>
                <span style={{ display: 'block' }}> {option.label}</span>
                {option.subLabel && (
                  <Typography
                    component="span"
                    sx={{
                      display: 'block',
                      marginTop: '-2px',
                      fontSize: '12px',
                      color: 'text.secondary',
                    }}
                  >
                    {option.subLabel}
                  </Typography>
                )}
              </Typography>
              {option.tooltip && (
                <Tooltip title={option.tooltip}>
                  <InfoOutlinedIcon />
                </Tooltip>
              )}
            </MenuItem>
          ))}
        </Select>
      )}
    </InputLabelText>
  );
};

export default SelectInput;
